import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { fetchProfile } from "../profileSlice";
import { useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  Typography,
  Paper,
  Divider,
  Stack,
  InputAdornment,
  Link,
  IconButton,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import LockIcon from "@mui/icons-material/Lock";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import axios from "axios";
import AppButton from "../components/Common/AppButton";

const styles = {
  root: {
    minHeight: "100vh",
    width: "100vw",
    background: `url('/login-background.png') no-repeat center center fixed`,
    backgroundSize: "cover",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  paper: {
    borderRadius: 2,
    px: { xs: 2, sm: 4 },
    py: { xs: 2.5, sm: 4 },
    maxWidth: 420,
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  logoContainer: {
    mb: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  logo: {
    width: 200,
    objectFit: "contain",
  },
  qcText: {
    variant: "h4",
    component: "span",
    fontWeight: 700,
    color: "#555",
    letterSpacing: 1,
  },
  subtitle: {
    fontWeight: 600,
    align: "center",
  },
  divider: {
    width: "100%",
  },
  form: {
    width: "100%",
  },
  link: {
    color: "#888",
  },
};

const validateEmail = (email) => {
  // Simple regex for demonstration
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError("");
    const errs = {};
    if (!email) errs.email = "Email is required";
    else if (!validateEmail(email)) errs.email = "Enter a valid email";
    if (!password) errs.password = "Password is required";
    setErrors(errs);

    if (Object.keys(errs).length === 0) {
      setLoading(true);
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/api/auth/login`,
          { email, password },
          { headers: { "Content-Type": "application/json" } }
        );

        localStorage.setItem("QC_accessToken", response.data.accessToken);
        // Fetch and store profile in Redux
        await dispatch(fetchProfile()).unwrap(); // Use unwrap() to get the actual result
        navigate("/dashboard", { replace: true });
      } catch (err) {
        console.error("Login error:", err);
        if (err.response && err.response.data && err.response.data.message) {
          setApiError(err.response.data.message || "Invalid credentials");
        } else {
          setApiError("Network error. Please try again.");
        }
        // Clear token if login failed
        localStorage.removeItem("QC_accessToken");
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Box sx={styles.root}>
      <Paper elevation={8} sx={styles.paper}>
        <Box sx={styles.logoContainer}>
          <img src="/austin-logo.png" alt="Austin Logo" style={styles.logo} />
          <Typography sx={styles.qcText}>QC</Typography>
        </Box>
        <Divider sx={{ ...styles.divider, mb: 1 }} />
        <Typography variant="subtitle1" sx={styles.subtitle}>
          Paperless Quality Control System
        </Typography>
        <Typography variant="body2" fontWeight={500} />
        <Divider sx={{ ...styles.divider, mt: 1, mb: 2 }} />
        <Typography variant="body2" align="center" mb={2}>
          Sign in and start managing your machines today!
        </Typography>
        <Box component="form" onSubmit={handleSubmit} sx={styles.form}>
          <Stack spacing={2}>
            <TextField
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={!!errors.email}
              helperText={errors.email}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon color="action" />
                  </InputAdornment>
                ),
              }}
              fullWidth
              autoComplete="email"
            />
            <TextField
              label="Password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={!!errors.password}
              helperText={errors.password}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                      onClick={() => setShowPassword((show) => !show)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              fullWidth
              autoComplete="current-password"
            />
            {apiError && (
              <Typography color="error" variant="body2" align="center">
                {apiError}
              </Typography>
            )}
            <AppButton
              type="submit"
              fullWidth
              disabled={loading}
              buttonType="primary"
              label={loading ? "Signing In..." : "SIGN IN"}
            />
          </Stack>
        </Box>
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{ width: "100%", mt: 2 }}
        >
          <Link
            component="button"
            variant="body2"
            underline="hover"
            sx={styles.link}
            onClick={() => navigate("/password/forgot")}
          >
            Forgot Password?
          </Link>
          <Link
            component="button"
            variant="body2"
            underline="hover"
            sx={styles.link}
            onClick={() => alert("Terms and Conditions")}
          >
            Terms and Conditions
          </Link>
        </Stack>
      </Paper>
    </Box>
  );
};

export default Login;
